import { compareTimestampInstants } from '../../lib/timestamp-instant';
import { isOffsetDateTime, isUuid } from '../../lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type AdminUserServiceStatus = 'ACTIVE' | 'SUSPENDED';
export type AdminUserLifecycleStatus = 'ACTIVE' | 'WITHDRAWING';

export interface AdminUser {
  user_id: string;
  service_status: AdminUserServiceStatus;
  lifecycle_status: AdminUserLifecycleStatus;
  registration_completed: boolean;
  role_bits: number | null;
  created_at: string;
  updated_at: string;
  /** Present only on the current versioned detail contract; absent on legacy rows. */
  version?: number;
}

export interface AdminUserCursor {
  beforeCreatedAt: string;
  beforeUserId: string;
}

export interface AdminUserFilters {
  limit?: number;
  beforeCreatedAt?: string;
  beforeUserId?: string;
}

export interface AdminUserPage {
  items: AdminUser[];
  nextCursor: AdminUserCursor | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string';
const canonicalUuid = (value: unknown): value is string =>
  isUuid(value) && value === value.toLowerCase();
const STATUSES: readonly string[] = ['ACTIVE', 'SUSPENDED'];
const LIFECYCLE_STATUSES: readonly string[] = ['ACTIVE', 'WITHDRAWING'];

function invalid(): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '사용자 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

function parseAdminUser(value: unknown, allowVersion = false): AdminUser {
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !==
      [
        'user_id',
        'service_status',
        'lifecycle_status',
        'registration_completed',
        'role_bits',
        'created_at',
        'updated_at',
        ...(allowVersion && Object.hasOwn(value, 'version') ? ['version'] : []),
      ]
        .sort()
        .join(',') ||
    (Object.hasOwn(value, 'version') &&
      (!Number.isSafeInteger(value.version) || Number(value.version) < 1)) ||
    !canonicalUuid(value.user_id) ||
    !text(value.service_status) ||
    !STATUSES.includes(value.service_status) ||
    !text(value.lifecycle_status) ||
    !LIFECYCLE_STATUSES.includes(value.lifecycle_status) ||
    typeof value.registration_completed !== 'boolean' ||
    !(
      value.role_bits === null ||
      (Number.isSafeInteger(value.role_bits) && Number(value.role_bits) >= 0)
    ) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at)
  )
    throw invalid();
  return value as unknown as AdminUser;
}

function parseCursor(value: unknown): AdminUserCursor | null {
  if (value === null) return null;
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !== 'before_created_at,before_user_id' ||
    !isOffsetDateTime(value.before_created_at) ||
    !canonicalUuid(value.before_user_id)
  )
    throw invalid();
  return { beforeCreatedAt: value.before_created_at, beforeUserId: value.before_user_id };
}

function validateFilters(filters: AdminUserFilters): void {
  if (
    Object.keys(filters).some(
      (key) => !['limit', 'beforeCreatedAt', 'beforeUserId'].includes(key),
    ) ||
    (filters.limit !== undefined &&
      (!Number.isSafeInteger(filters.limit) || filters.limit < 1 || filters.limit > 50)) ||
    (filters.beforeCreatedAt === undefined) !== (filters.beforeUserId === undefined) ||
    (filters.beforeCreatedAt !== undefined &&
      (!isOffsetDateTime(filters.beforeCreatedAt) || filters.beforeCreatedAt.length > 64)) ||
    (filters.beforeUserId !== undefined && !canonicalUuid(filters.beforeUserId))
  )
    throw invalid();
}

function isStrictlyAfterCursor(next: AdminUserCursor, current: AdminUserFilters): boolean {
  if (current.beforeCreatedAt === undefined || current.beforeUserId === undefined) return true;
  const timeOrder = compareTimestampInstants(next.beforeCreatedAt, current.beforeCreatedAt);
  return timeOrder < 0 || (timeOrder === 0 && next.beforeUserId < current.beforeUserId);
}

export async function readAdminUsers(
  client: ServiceClient,
  filters: AdminUserFilters = {},
  signal?: AbortSignal,
): Promise<AdminUserPage> {
  validateFilters(filters);
  const value = await client.get<unknown>('admin/users', {
    params: {
      limit: filters.limit ?? 50,
      ...(filters.beforeCreatedAt === undefined
        ? {}
        : { before_created_at: filters.beforeCreatedAt }),
      ...(filters.beforeUserId === undefined ? {} : { before_user_id: filters.beforeUserId }),
    },
    signal,
  });
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !== 'items,next_cursor' ||
    !Array.isArray(value.items) ||
    value.items.length > (filters.limit ?? 50)
  )
    throw invalid();
  const items = value.items.map((item) => parseAdminUser(item));
  const nextCursor = parseCursor(value.next_cursor);
  if (nextCursor) {
    if (
      nextCursor.beforeUserId !== items.at(-1)?.user_id ||
      compareTimestampInstants(
        nextCursor.beforeCreatedAt,
        items.at(-1)?.created_at ?? nextCursor.beforeCreatedAt,
      ) !== 0 ||
      !isStrictlyAfterCursor(nextCursor, filters)
    )
      throw invalid();
  }
  return { items, nextCursor };
}

export async function readAdminUser(
  client: ServiceClient,
  userId: string,
  signal?: AbortSignal,
): Promise<AdminUser> {
  if (!canonicalUuid(userId)) throw invalid();
  const value = await client.get<unknown>(`admin/users/${userId}`, { signal });
  const user = parseAdminUser(value, true);
  if (user.user_id !== userId) throw invalid();
  return user;
}

export interface AdminUserAuthorization {
  user_id: string;
  assigned: boolean;
  role_bits: number;
  domain_overrides: Record<string, number>;
  domain_bits: Record<string, number>;
  registry_version: number | null;
  revision: number | null;
}

const DOMAIN_MASKS: Record<string, (version: number) => number> = {
  inventory: () => 31,
  booking: (v) => [3, 31, 63, 63, 127, 255, 255, 255][v - 1] ?? 0,
  user: (v) => (v >= 2 ? 7 : 3),
  property: (v) => (v >= 8 ? 7 : 3),
  order: () => 1,
  payment: () => 3,
  entitlement: (v) => (v >= 7 ? 3 : v >= 4 ? 1 : 0),
};
const MAX_MASK = Number.MAX_SAFE_INTEGER;
function parseMaskMap(
  value: unknown,
  maxDomains: number,
  version?: number,
): Record<string, number> {
  if (!record(value) || Object.keys(value).length > maxDomains) throw invalid();
  const result: Record<string, number> = {};
  for (const [domain, bits] of Object.entries(value)) {
    const max = version === undefined ? MAX_MASK : DOMAIN_MASKS[domain]?.(version);
    if (
      max === undefined ||
      !Number.isSafeInteger(bits) ||
      Number(bits) < 0 ||
      (Number(bits) & ~max) !== 0
    )
      throw invalid();
    result[domain] = Number(bits);
  }
  return result;
}

export async function readAdminUserAuthorization(
  client: ServiceClient,
  userId: string,
  signal?: AbortSignal,
): Promise<AdminUserAuthorization> {
  if (!canonicalUuid(userId)) throw invalid();
  const value = await client.get<unknown>(`admin/users/${userId}/authorization`, { signal });
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !==
      'assigned,domain_bits,domain_overrides,registry_version,revision,role_bits,user_id' ||
    value.user_id !== userId ||
    typeof value.assigned !== 'boolean' ||
    !Number.isSafeInteger(value.role_bits) ||
    Number(value.role_bits) < 0 ||
    Number(value.role_bits) > 15 ||
    !(
      value.registry_version === null ||
      (Number.isSafeInteger(value.registry_version) &&
        Number(value.registry_version) >= 1 &&
        Number(value.registry_version) <= 8)
    ) ||
    !(
      value.revision === null ||
      (Number.isSafeInteger(value.revision) && Number(value.revision) > 0)
    )
  )
    throw invalid();
  const version = value.registry_version === null ? undefined : Number(value.registry_version);
  const overrides = parseMaskMap(value.domain_overrides, 4, version);
  // Stored overrides are validated against their source registry; effective grants
  // are always the current projection, even when the stored root is legacy.
  const bits = parseMaskMap(value.domain_bits, 7, 8);
  if (
    !value.assigned &&
    (value.role_bits !== 0 ||
      Object.keys(overrides).length ||
      Object.keys(bits).length ||
      version !== undefined ||
      value.revision !== null)
  )
    throw invalid();
  if (value.assigned && (version === undefined || value.revision === null)) throw invalid();
  return {
    user_id: userId,
    assigned: value.assigned,
    role_bits: Number(value.role_bits),
    domain_overrides: overrides,
    domain_bits: bits,
    registry_version: version ?? null,
    revision: value.revision as number | null,
  };
}

export interface AdminUserStatusReceipt {
  command_id: string;
  user_id: string;
  user_version: number;
  service_status: AdminUserServiceStatus;
  audit_id: string | null;
  changed: boolean;
}
export async function changeAdminUserStatus(
  client: ServiceClient,
  userId: string,
  body: {
    command_id: string;
    expected_version: number;
    reason: string;
    service_status: AdminUserServiceStatus;
  },
): Promise<AdminUserStatusReceipt> {
  if (
    !canonicalUuid(userId) ||
    !isUuid(body.command_id) ||
    !Number.isSafeInteger(body.expected_version) ||
    body.expected_version < 1 ||
    !body.reason.trim() ||
    !STATUSES.includes(body.service_status)
  )
    throw invalid();
  const value = await client.patch<unknown>(`admin/users/${userId}/status`, { json: body });
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !==
      'audit_id,changed,command_id,service_status,user_id,user_version' ||
    value.command_id !== body.command_id ||
    value.user_id !== userId ||
    !Number.isSafeInteger(value.user_version) ||
    Number(value.user_version) < 1 ||
    value.service_status !== body.service_status ||
    !(value.audit_id === null || canonicalUuid(value.audit_id)) ||
    typeof value.changed !== 'boolean' ||
    value.changed !== (value.audit_id !== null)
  )
    throw invalid();
  return value as unknown as AdminUserStatusReceipt;
}

export async function changeAdminUserAuthorization(
  client: ServiceClient,
  userId: string,
  body: {
    command_id: string;
    expected_revision: number;
    role_bits: number;
    domain_overrides: Record<string, number>;
    reason: string;
  },
  registryVersion?: number | null,
): Promise<{ command_id: string; revision: number }> {
  if (
    !canonicalUuid(userId) ||
    !isUuid(body.command_id) ||
    !Number.isSafeInteger(body.expected_revision) ||
    body.expected_revision < 0 ||
    !Number.isSafeInteger(body.role_bits) ||
    body.role_bits < 0 ||
    body.role_bits > 15 ||
    !body.reason.trim() ||
    body.reason.length > 500
  )
    throw invalid();
  if (registryVersion === null && Object.keys(body.domain_overrides).length > 0) throw invalid();
  parseMaskMap(body.domain_overrides, 4, registryVersion ?? undefined);
  const value = await client.patch<unknown>(`admin/users/${userId}/authorization`, { json: body });
  if (
    !record(value) ||
    Object.keys(value).sort().join(',') !== 'command_id,revision' ||
    value.command_id !== body.command_id ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 1
  )
    throw invalid();
  return value as { command_id: string; revision: number };
}
